import {
  ConnectedPosition,
  FlexibleConnectedPositionStrategy,
  GlobalPositionStrategy,
  Overlay,
  OverlayPositionBuilder,
  OverlayRef,
} from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';
import {
  DestroyRef,
  Directive,
  ElementRef,
  HostListener,
  Injector,
  OnChanges,
  OnDestroy,
  SimpleChanges,
  TemplateRef,
  computed,
  inject,
  input,
  output,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Observable, map, of, skip, takeUntil } from 'rxjs';

import { DEFAULT_MOBILE_DIALOG_MAX_WIDTH } from '@shared/constants';
import { BreakpointService } from '@shared/services/breakpoint';
import { isDefined } from '@shared/utils/functions';

import { ArrowPosition } from './tooltip-data.interface';
import { TooltipRef } from './tooltip-ref';
import { TooltipComponent } from './tooltip.component';
import { TOOLTIP_DATA } from './tooltip.tokens';

const DEFAULT_OFFSET = 2; // 2px

export type TooltipPosition = 'top' | 'bottom' | 'left' | 'right';
export type TooltipTrigger = 'click' | 'hover';
export type TooltipVariant = 'info' | 'error' | 'warning' | 'success';

@Directive({
  selector: '[appTooltip]',
})
export class TooltipDirective implements OnChanges, OnDestroy {
  readonly #overlay = inject(Overlay);
  readonly #overlayPositionBuilder = inject(OverlayPositionBuilder);
  readonly #elementRef = inject(ElementRef<HTMLElement>);
  readonly #injector = inject(Injector);
  readonly #breakpointService = inject(BreakpointService);
  readonly #destroyRef = inject(DestroyRef);

  public readonly tooltip = input<string | TemplateRef<unknown>>('', {
    alias: 'appTooltip',
  });
  public readonly title = input('');
  public readonly position = input<TooltipPosition>('bottom');
  public readonly trigger = input<TooltipTrigger>('hover');
  public readonly variant = input<TooltipVariant>('info');
  public readonly styleClasses = input('');

  public readonly tooltipClose = output<void>();

  public readonly isMobile = this.#breakpointService.isMobile;

  readonly #scrollStrategy = this.#overlay.scrollStrategies.close();
  #positionStrategy = this.createPositionStrategy();
  #overlayRef?: OverlayRef;
  #tooltipRef?: TooltipRef;
  #shouldBeShown = false;

  readonly #appliedTrigger = computed(() => {
    const trigger = this.trigger();
    const isMobile = this.isMobile();
    if (trigger === 'click' || isMobile) {
      return 'click';
    }
    return 'hover';
  });

  constructor() {
    this.resetOnBreakpointChange();
  }

  @HostListener('click')
  public onClick(): void {
    if (this.#appliedTrigger() !== 'click') {
      return;
    }

    this.#shouldBeShown = true;
    this.show();
  }

  @HostListener('mouseenter')
  public onMouseEnter(): void {
    if (this.#appliedTrigger() !== 'hover') {
      return;
    }

    this.#shouldBeShown = true;
    this.show();
  }

  @HostListener('mouseleave')
  public onMouseLeave(): void {
    if (this.#appliedTrigger() !== 'hover' || this.#tooltipRef === null || this.#tooltipRef === undefined) {
      return;
    }

    this.close();
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes['position']) {
      this.updatePositionStrategy();
    }
    if (changes['tooltip'] && this.#shouldBeShown) {
      this.show();
    }
  }

  public ngOnDestroy(): void {
    if (!this.#tooltipRef) return;
    this.close();
  }

  private show(): void {
    if (!this.tooltip()) {
      return;
    }

    if (!isDefined(this.#overlayRef)) {
      this.#overlayRef = this.createOverlayRef();
    } else {
      this.#overlayRef.detach();
    }

    this.#tooltipRef = new TooltipRef(this.#overlayRef);
    this.#tooltipRef.afterClosed$().subscribe(() => {
      this.#tooltipRef = undefined;
    });

    this.#overlayRef.attach(new ComponentPortal(TooltipComponent, undefined, this.createInjector(this.#tooltipRef)));
    this.#overlayRef
      .backdropClick()
      .pipe(takeUntil(this.#tooltipRef.afterClosed$()))
      .subscribe(() => this.close());
  }

  private updatePositionStrategy(): void {
    this.#positionStrategy = this.createPositionStrategy();
    if (this.#overlayRef) {
      this.#overlayRef.updatePositionStrategy(this.#positionStrategy);
    }
  }

  private resetOnBreakpointChange(): void {
    this.#breakpointService.isMobile$.pipe(skip(1), takeUntilDestroyed(this.#destroyRef)).subscribe(() => {
      this.#tooltipRef?.close();
      this.#overlayRef?.dispose();
      this.#overlayRef = undefined;
      this.#positionStrategy = this.createPositionStrategy();
    });
  }

  private createPositionStrategy(): GlobalPositionStrategy | FlexibleConnectedPositionStrategy {
    return this.#breakpointService.isMobile()
      ? this.#overlayPositionBuilder.global().centerHorizontally().centerVertically()
      : this.#overlayPositionBuilder.flexibleConnectedTo(this.#elementRef).withPositions(this.getPositions());
  }

  private getPositions(): ConnectedPosition[] {
    const position = this.position();
    if (position === 'top' || position === 'bottom') {
      const mostDesiredPosition: ConnectedPosition = {
        originX: 'center',
        originY: position,
        overlayX: 'center',
        overlayY: position === 'top' ? 'bottom' : 'top',
        offsetY: position === 'top' ? -DEFAULT_OFFSET : DEFAULT_OFFSET,
      };
      return [
        mostDesiredPosition,
        { ...mostDesiredPosition, offsetX: 0, originX: 'start' },
        {
          ...mostDesiredPosition,
          originY: position === 'top' ? 'bottom' : 'top',
          overlayY: position,
          offsetY: position === 'top' ? DEFAULT_OFFSET : -DEFAULT_OFFSET,
        },
        {
          ...mostDesiredPosition,
          originY: position === 'top' ? 'bottom' : 'top',
          overlayY: position,
          offsetX: 0,
          offsetY: position === 'top' ? DEFAULT_OFFSET : -DEFAULT_OFFSET,
        },
      ];
    }
    if (position === 'right' || position === 'left') {
      const mostDesiredPosition: ConnectedPosition = {
        originX: position === 'right' ? 'end' : 'start',
        originY: 'center',
        overlayX: position === 'right' ? 'start' : 'end',
        overlayY: 'center',
        offsetX: position === 'right' ? DEFAULT_OFFSET : -DEFAULT_OFFSET,
        offsetY: 2,
      };
      return [
        mostDesiredPosition,
        {
          ...mostDesiredPosition,
          originX: position === 'right' ? 'start' : 'end',
          overlayX: position === 'right' ? 'end' : 'start',
          offsetX: position === 'right' ? -DEFAULT_OFFSET : DEFAULT_OFFSET,
        },
      ];
    }
    return [];
  }

  private createOverlayRef(): OverlayRef {
    return this.#overlay.create({
      positionStrategy: this.#positionStrategy,
      scrollStrategy: this.#scrollStrategy,
      hasBackdrop: this.#appliedTrigger() === 'click',
      backdropClass: this.#breakpointService.isMobile() ? 'bg-primary-500/80' : '',
      width: this.#breakpointService.isMobile() ? '22rem' : 'auto',
      maxWidth: this.#breakpointService.isMobile() ? DEFAULT_MOBILE_DIALOG_MAX_WIDTH : 'auto',
    });
  }

  private getArrowPosition$(): Observable<ArrowPosition | undefined> {
    if (this.#positionStrategy instanceof GlobalPositionStrategy) {
      return of();
    }

    return this.#positionStrategy.positionChanges.pipe(
      map((positionChange) => {
        if (this.#overlayRef === null || this.#overlayRef === undefined) {
          return;
        }
        const element: HTMLElement = this.#elementRef.nativeElement;
        const hostClientRect = element.getBoundingClientRect();
        const overlayClientRect = this.#overlayRef.overlayElement.getBoundingClientRect();
        if (
          (positionChange.connectionPair.overlayY === 'top' && positionChange.connectionPair.originY === 'bottom') ||
          (positionChange.connectionPair.overlayY === 'bottom' && positionChange.connectionPair.originY === 'top')
        ) {
          const offsetX = hostClientRect.x - overlayClientRect.x + hostClientRect.width / 2;
          const position = positionChange.connectionPair.originY as 'top' | 'bottom';
          return { position, offsetX };
        }
        if (
          (positionChange.connectionPair.overlayX === 'start' && positionChange.connectionPair.originX === 'end') ||
          (positionChange.connectionPair.overlayX === 'end' && positionChange.connectionPair.originX === 'start')
        ) {
          const offsetY = hostClientRect.y - overlayClientRect.y + hostClientRect.height / 2;
          const position = positionChange.connectionPair.originX as 'start' | 'end';
          return { position, offsetY };
        }
        return void 0;
      })
    );
  }

  private createInjector(tooltipRef: TooltipRef): Injector {
    return Injector.create({
      providers: [
        {
          provide: TOOLTIP_DATA,
          useValue: {
            tooltip: this.tooltip(),
            title: this.title(),
            arrowPosition$: this.getArrowPosition$(),
            variant: this.variant(),
            styleClasses: this.styleClasses(),
          },
        },
        {
          provide: TooltipRef,
          useValue: tooltipRef,
        },
      ],
      parent: this.#injector,
    });
  }

  private close(): void {
    if (!isDefined(this.#tooltipRef)) return;

    this.#shouldBeShown = false;
    this.#tooltipRef.close();
    this.tooltipClose.emit();
  }
}
